import type { User } from '@gmn-portal/backend';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type EntityPermissionOverviewEntry,
  useEntityPermissionOverview,
  useUser,
} from '@/core/api';
import type { AclServiceKey } from '@/shared/utils/permissions';

export const VIEW_AS_QUERY_KEY = 'viewAs';

type UseViewAsStateParams = {
  entityType: AclServiceKey;
  entityId?: string | null;
  enabled: boolean;
};

type UseViewAsStateResult = {
  viewAsUserId: string | null;
  viewAsUser: User | null;
  viewAsAccess: EntityPermissionOverviewEntry | null;
  isViewAsActive: boolean;
  isViewAsLoading: boolean;
  setViewAsUserId: (userId: string) => void;
  clearViewAs: () => void;
};

const getAccessEntry = (
  entries: EntityPermissionOverviewEntry[] | undefined,
  userId: string | null
) => {
  if (!entries || !userId) return null;
  return entries.find((entry) => entry.userId === userId) ?? null;
};

export function useViewAsState({
  entityType,
  entityId,
  enabled,
}: UseViewAsStateParams): UseViewAsStateResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewAsUserId = searchParams.get(VIEW_AS_QUERY_KEY);

  const { data: viewAsUser, isLoading: isLoadingUser } = useUser(viewAsUserId ?? '', {
    disabled: !viewAsUserId,
  });

  const {
    data: overviewData,
    isFetching: isFetchingOverview,
    isLoading: isLoadingOverview,
  } = useEntityPermissionOverview({
    entityType,
    entityId: entityId ?? '',
    userIds: viewAsUserId ? [viewAsUserId] : [],
    disabled: !enabled || !entityId || !viewAsUserId,
  });

  const viewAsAccess = useMemo(
    () => getAccessEntry(overviewData?.entries, viewAsUserId),
    [overviewData?.entries, viewAsUserId]
  );

  const hasReadAccess = Boolean(viewAsAccess?.read.allowed);

  const updateQueryParam = useCallback(
    (nextId: string | null) => {
      const nextParams = new URLSearchParams(searchParams);
      if (nextId) {
        nextParams.set(VIEW_AS_QUERY_KEY, nextId);
      } else {
        nextParams.delete(VIEW_AS_QUERY_KEY);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearViewAs = useCallback(() => {
    updateQueryParam(null);
  }, [updateQueryParam]);

  useEffect(() => {
    if (!enabled && viewAsUserId) {
      clearViewAs();
    }
  }, [enabled, viewAsUserId, clearViewAs]);

  const previousEntityIdRef = useRef<string | null>(entityId ?? null);
  useEffect(() => {
    if (!viewAsUserId) {
      previousEntityIdRef.current = entityId ?? null;
      return;
    }

    if (previousEntityIdRef.current && entityId && previousEntityIdRef.current !== entityId) {
      clearViewAs();
    }

    previousEntityIdRef.current = entityId ?? null;
  }, [entityId, viewAsUserId, clearViewAs]);

  useEffect(() => {
    if (!viewAsUserId) return;
    if (!entityId) {
      clearViewAs();
      return;
    }

    if (isLoadingUser || isLoadingOverview || isFetchingOverview) return;

    if (!viewAsUser) {
      clearViewAs();
      return;
    }

    if (!hasReadAccess) {
      clearViewAs();
    }
  }, [
    viewAsUserId,
    entityId,
    isLoadingUser,
    isLoadingOverview,
    isFetchingOverview,
    viewAsUser,
    hasReadAccess,
    clearViewAs,
  ]);

  const isViewAsActive = enabled && Boolean(viewAsUserId && viewAsUser && hasReadAccess);

  return {
    viewAsUserId: viewAsUserId ?? null,
    viewAsUser: viewAsUser ?? null,
    viewAsAccess,
    isViewAsActive,
    isViewAsLoading: isLoadingUser || isLoadingOverview || isFetchingOverview,
    setViewAsUserId: (userId) => updateQueryParam(userId),
    clearViewAs,
  };
}
